import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FormLabel } from "../../shared/Input/FormLabel";
import Spinner from "../../shared/Spinner";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useDebounceSearch } from "../../hooks/useDebounceSearch";
import { DriverCreateModal } from "../../modals";
import { useGetDriversQuery } from "../../redux/services/drivers/driversApi";

const DEFAULT_LIMIT = 100;

/**
 * Build a human readable label for a driver-shaped object.
 *
 * @param {Object} driver - Driver document (fullName/firstName/lastName/etc.)
 * @returns {string} Display name for the driver.
 */
function getDriverName(driver = {}) {
  const firstLastName = [driver.firstName, driver.lastName]
    .filter(Boolean)
    .join(" ");

  return driver.fullName || firstLastName || driver.name || driver.email || "";
}

/**
 * The DriverCreateModal may yield a raw driver, a wrapped {data} envelope, or
 * a {user}/{driver} payload. Normalize all of those down to a plain driver.
 *
 * @param {Object} response - Raw response from the create driver flow.
 * @returns {Object} A plain driver-shaped object.
 */
function normalizeDriverResponse(response = {}) {
  return (
    response.driver ||
    response.user ||
    response.data?.driver ||
    response.data?.user ||
    response.data ||
    response
  );
}

/**
 * Convert a driver document into the {label, value, ...} option shape this
 * component (and its onChange consumers) expect.
 *
 * @param {Object} driver - Driver document.
 * @returns {{label:string,value:Object,fullName:string,email?:string,phone?:string}}
 */
function buildDriverOption(driver) {
  const label = getDriverName(driver);

  return {
    label,
    value: driver,
    fullName: label,
    email: driver.email,
    phone: driver.phone,
  };
}

/**
 * Callers may pass either pre-built options ({label,value}) or raw driver
 * documents via the `options` fallback prop. Coerce both into the option shape.
 *
 * @param {Object} option - Provided option or raw driver document.
 * @returns {?Object} Normalized option, or null if invalid.
 */
function normalizeProvidedOption(option) {
  if (!option) return null;
  if (option.label && option.value) return option;

  return buildDriverOption(option);
}

/**
 * Pull a driver object out of whatever the parent passed as `value`. Supports:
 *   - A driver document ({_id, fullName, ...})
 *   - An option wrapper ({label, value: driverDoc})
 *   - A plain string (legacy: just the name)
 *   - Empty/falsy
 *
 * @param {*} value - Caller-provided value.
 * @returns {?Object} Driver-shaped object, or null when there's no selection.
 */
function resolveSelectedDriver(value) {
  if (!value) return null;
  if (typeof value === "string") return { fullName: value };
  if (value.value && typeof value.value === "object") return value.value;

  return value;
}

/**
 * Stable identifier for an option so we can compare selections and key the
 * DOM list without relying on array indices.
 *
 * @param {Object} option - Driver option.
 * @returns {string} A stable id derived from the driver document.
 */
function getOptionId(option) {
  return (
    option?.value?._id ||
    option?.value?.id ||
    option?.value?.email ||
    option?.label ||
    ""
  );
}

/**
 * DriverSelect
 *
 * A self-contained driver picker with server-side search powered by RTK Query.
 * Renders a labeled combobox: an input you can type into, a dropdown of driver
 * results, an optional "Create new driver" action, and inline loading/clear
 * controls.
 *
 * Design notes:
 * - The input is NEVER disabled by loading state. It only goes disabled when
 *   the caller explicitly passes `disabled`. The inline spinner is the
 *   loading indicator. Disabling the input during a refetch was previously
 *   blurring the user mid-typing whenever the search produced a new RTK
 *   Query cache key.
 * - The dropdown filters client-side as the user types so the list narrows
 *   instantly. The 500ms debounced server search then refines results with
 *   email/phone hits the client-side filter can't see.
 * - "Create new driver" only appears when the search returns zero matching
 *   drivers (server + client filter both empty). Browsing with an empty
 *   search hides it, and any partial match hides it too.
 * - The input's display text shows the selected driver's name when the picker
 *   is idle, and shows the active search string while the dropdown is open.
 *   This keeps focus and the caret stable across debounced refetches.
 * - `onChange` payload shape mirrors the previous implementation so callers
 *   don't need to change.
 *
 * @component
 */
export const DriverSelect = ({
  id = "driver",
  value,
  onChange,
  label = "Driver",
  placeholder = "Select Driver",
  size,
  error,
  required,
  className,
  wrapperClasses,
  disabled = false,
  allowCreate = true,
  queryParams = {},
  limit = DEFAULT_LIMIT,
  onDriverCreated,
  options: providedOptions = [],
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [createVisible, setCreateVisible] = useState(false);

  const { search, handleSearch, clearSearch } = useDebounceSearch();

  const { data, isLoading, isFetching, refetch } = useGetDriversQuery(
    {
      page: 1,
      limit,
      search: search.useSearch,
      searchTerm: search.term,
      ...queryParams,
    },
    { refetchOnMountOrArgChange: true }
  );

  const selectedDriver = useMemo(() => resolveSelectedDriver(value), [value]);
  const selectedLabel = selectedDriver ? getDriverName(selectedDriver) : "";

  /**
   * Merge fetched + caller-provided options, de-duped by stable id. Caller
   * options act as a fallback so the picker can still show a selection even
   * before the network call finishes.
   */
  const driverOptions = useMemo(() => {
    const fetched = (data?.drivers || []).filter(Boolean).map(buildDriverOption);
    const safeProvided = Array.isArray(providedOptions) ? providedOptions : [];
    const fallback = safeProvided.map(normalizeProvidedOption).filter(Boolean);

    const byId = new Map();
    [...fallback, ...fetched].forEach((option) => {
      const key = getOptionId(option);
      if (option.label && key) byId.set(key, option);
    });

    return Array.from(byId.values());
  }, [data, providedOptions]);

  const trimmedInput = inputValue.trim();

  /**
   * Client-side filter so the dropdown reacts instantly to typing without
   * waiting for the 500ms debounced server search. Once the server search
   * lands, `driverOptions` already contains the narrower set so this filter
   * becomes a no-op for those queries.
   */
  const visibleOptions = useMemo(() => {
    if (!trimmedInput) return driverOptions;
    const needle = trimmedInput.toLowerCase();
    return driverOptions.filter((option) => {
      const label = (option.label || "").toLowerCase();
      const email = (option.email || "").toLowerCase();
      const phone = (option.phone || "").toLowerCase();
      return (
        label.includes(needle) ||
        email.includes(needle) ||
        phone.includes(needle)
      );
    });
  }, [driverOptions, trimmedInput]);

  // Reset the highlighted item whenever the result list or open state changes.
  useEffect(() => {
    setActiveIndex(visibleOptions.length > 0 ? 0 : -1);
  }, [visibleOptions.length, isOpen]);

  /**
   * When the dropdown closes (via outside click, selection, escape, etc.) the
   * input should snap back to showing the selected driver's name and any
   * in-flight search should be cleared.
   */
  function closePicker({ commitSearchClear = true } = {}) {
    setIsOpen(false);
    setActiveIndex(-1);
    setInputValue("");
    if (commitSearchClear) clearSearch();
  }

  useOutsideClick(containerRef, () => {
    if (isOpen) closePicker();
  });

  /**
   * Fire the parent onChange with the same payload shape callers have always
   * received: the option fields, plus {id, data, fullValue}.
   */
  function emitChange(option) {
    if (!onChange) return;
    onChange({
      ...option,
      id,
      data: option,
      fullValue: option.label,
    });
  }

  function selectOption(option) {
    emitChange(option);
    closePicker();
    inputRef.current?.blur?.();
  }

  function handleClearSelection(event) {
    event.preventDefault();
    event.stopPropagation();
    setInputValue("");
    clearSearch();
    if (onChange) {
      // Emit `null` (not "") for value/data so the parent form's
      // dot-path lookups (e.g. Object.byString(form, "driver.fullName"))
      // see a nullable intermediate and short-circuit instead of trying
      // `'fullName' in ''`, which throws TypeError on string primitives.
      onChange({ id, label: "", value: null, data: null, fullValue: "" });
    }
  }

  function handleInputChange(event) {
    const next = event.target.value;
    setInputValue(next);
    setIsOpen(true);
    handleSearch(next);
  }

  function handleInputFocus(event) {
    if (disabled) return;
    setIsOpen(true);
    // Preload the input with the current selection so the user can simply
    // start typing to replace it. Selecting the text makes the "type to
    // replace" interaction feel natural.
    if (selectedLabel && !inputValue) {
      setInputValue(selectedLabel);
      // Defer to next tick so React commits the value before we select it.
      const node = event.target;
      setTimeout(() => node?.select?.(), 0);
    }
  }

  function handleKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) =>
        visibleOptions.length === 0
          ? -1
          : Math.min(i + 1, visibleOptions.length - 1)
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? 0 : i - 1));
    } else if (event.key === "Enter") {
      if (isOpen && activeIndex >= 0 && visibleOptions[activeIndex]) {
        event.preventDefault();
        selectOption(visibleOptions[activeIndex]);
      }
    } else if (event.key === "Escape") {
      closePicker();
      inputRef.current?.blur?.();
    }
  }

  function handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    if (isOpen) {
      closePicker();
    } else {
      setIsOpen(true);
      inputRef.current?.focus?.();
    }
  }

  function handleCreateClick(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setCreateVisible(true);
  }

  function handleDriverCreated(response) {
    const driver = normalizeDriverResponse(response);
    const option = buildDriverOption(driver);

    refetch();
    onDriverCreated && onDriverCreated(driver);

    if (option.label) selectOption(option);
  }

  // Display logic: while the picker is open, the input mirrors the user's
  // search string. While closed, it mirrors the selected driver's name.
  const displayValue = isOpen ? inputValue : selectedLabel;

  // Any in-flight network activity (initial load OR debounced search refetch).
  // Drives the inline spinner inside the input — which is the ONLY loading
  // indicator. RTK Query flips `isLoading` back to true whenever the args
  // change to a new cache key (each search term is a new key), so we
  // intentionally do NOT derive the input's disabled state from it.
  const isBusy = isLoading || isFetching;
  const inputDisabled = Boolean(disabled);
  const hasSelection = Boolean(selectedLabel);
  const showError = Boolean(error);

  // "Create new driver" should only appear when the user has typed something
  // AND the search (server + client filter) returned zero matching drivers.
  // As long as ANY driver matched, we assume the user can pick from the list
  // instead of creating a duplicate.
  const showCreateButton =
    allowCreate && trimmedInput.length > 0 && visibleOptions.length === 0;

  const sizeLg = size === "lg";
  const sizeSm = size === "sm";
  const sizeMd = !sizeLg && !sizeSm;

  const inputClasses = classNames(
    className,
    "shadow-sm bg-white block w-full border rounded-lg pr-16",
    {
      "p-4 sm:text-md": sizeLg,
      "p-2.5 text-sm": sizeMd,
      "p-2 sm:text-xs": sizeSm,
      "bg-gray-100 cursor-not-allowed": inputDisabled,
      "border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-gray-900":
        !showError && !inputDisabled,
      "bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500":
        showError,
    }
  );

  const wrapperClassesAll = classNames("block", wrapperClasses);
  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  function renderEmptyState() {
    // While anything is loading, stay silent and let the input's inline
    // spinner be the only loading indicator. This avoids flashing a
    // premature "no results" message before the search lands.
    if (isBusy) return null;
    if (visibleOptions.length > 0) return null;
    return (
      <div className="p-2.5 text-sm text-gray-500">
        {trimmedInput ? "No drivers match this search" : "No drivers found"}
      </div>
    );
  }

  return (
    <>
      <div className={wrapperClassesAll}>
        {label && (
          <FormLabel htmlFor={id} required={required}>
            {label}
          </FormLabel>
        )}

        <div ref={containerRef} className="relative w-full">
          <input
            ref={inputRef}
            type="text"
            id={id}
            className={inputClasses}
            placeholder={placeholder}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={inputDisabled}
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={`${id}-listbox`}
          />

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
            {hasSelection && !isOpen && !inputDisabled && !isBusy && (
              <button
                type="button"
                aria-label="Clear driver"
                className="p-1 text-gray-400 hover:text-gray-600"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClearSelection}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
            {isBusy ? (
              <span
                aria-label="Loading drivers"
                role="status"
                className="flex items-center p-1"
              >
                <Spinner
                  size="xs"
                  className="text-gray-400 fill-primary-500"
                />
              </span>
            ) : (
              <button
                type="button"
                aria-label={isOpen ? "Close drivers" : "Open drivers"}
                className="p-1 text-gray-500 hover:text-gray-700"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleToggleClick}
                disabled={inputDisabled}
              >
                <ChevronIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {isOpen && (
            <div
              id={`${id}-listbox`}
              role="listbox"
              className="shadow rounded-b max-h-56 overflow-y-auto text-sm bg-white absolute border border-t-0 left-0 right-0 z-50"
            >
              {showCreateButton && (
                <button
                  type="button"
                  className="w-full text-left p-2 text-sm font-medium text-primary-600 hover:bg-gray-100 border-b border-gray-100"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleCreateClick}
                >
                  Create new driver "{trimmedInput}"
                </button>
              )}

              {renderEmptyState()}

              {visibleOptions.map((option, index) => {
                const optionId = getOptionId(option) || String(index);
                const isActive = index === activeIndex;
                const selectedId = selectedDriver
                  ? selectedDriver._id ||
                    selectedDriver.id ||
                    selectedDriver.email ||
                    selectedLabel
                  : null;
                const isSelected =
                  selectedId != null && optionId === selectedId;

                const optionClasses = classNames(
                  "p-2.5 text-sm cursor-pointer",
                  isActive ? "bg-gray-100" : "hover:bg-gray-100",
                  isSelected && "font-semibold text-primary-700"
                );

                return (
                  <div
                    key={optionId}
                    role="option"
                    aria-selected={isSelected}
                    className={optionClasses}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => selectOption(option)}
                  >
                    {option.body || option.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showError && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      {allowCreate && (
        <DriverCreateModal
          visible={createVisible}
          setVisible={setCreateVisible}
          onDriverCreated={handleDriverCreated}
        />
      )}
    </>
  );
};

export default DriverSelect;
